import { z } from 'zod';
import { expectDefined, isEmpty } from './util';

export const DataSchema: z.ZodType<Data, z.ZodTypeDef, unknown> = z
  .string()
  .optional()
  .transform(s => Data.fromBase64(isEmpty(s) ? '' : expectDefined(s)));

export function encodeBase64(input: Uint8Array | ArrayBuffer): string {
  let bytes: Uint8Array;
  if (input instanceof ArrayBuffer) {
    bytes = new Uint8Array(input);
  } else {
    bytes = input;
  }

  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

export function decodeBase64(base64: string): Uint8Array {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

export class Data extends Uint8Array {
  constructor(bytes?: Uint8Array | number[]) {
    if (bytes) {
      if (bytes instanceof Uint8Array) {
        super(bytes);
      } else if (
        Array.isArray(bytes) &&
        bytes.every(num => Number.isInteger(num) && num >= 0 && num <= 255)
      ) {
        super(new Uint8Array(bytes));
      } else {
        throw new Error(
          'Invalid input. Expected Uint8Array or number[] in the range 0-255.'
        );
      }
    } else {
      super(new ArrayBuffer(0));
    }
  }

  static fromBase64(base64: string): Data {
    return new Data(decodeBase64(base64));
  }

  toBase64(): string {
    return encodeBase64(this);
  }

  toJSON(): string {
    return this.toBase64();
  }

  static fromJSON(json: string): Data {
    if (typeof json !== 'string') {
      throw new Error('Expected string');
    }
    return Data.fromBase64(json);
  }

  equals(data: Data): boolean {
    if (this === data) {
      return true;
    }
    if (this.byteLength !== data.byteLength) {
      return false;
    }
    for (let i = 0; i < this.byteLength; i++) {
      if (this[i] !== data[i]) {
        return false;
      }
    }
    return true;
  }
}
